import React, { useEffect } from 'react';
import { FontWeights, IPersonaStyles, IStackTokens, Persona, PersonaSize, Stack } from '@fluentui/react';
import { pageDataService } from 'common/utilities';
import { useLocalizedStrings } from 'i18n';
import { useAppDispatch, useAppSelector } from 'store';
import { getFamilyMembersAsync } from 'store/family/familySlice';

interface IFamilyPersonaListProps {
  /**
   * Optional role to filter the list of family members by.
   */
  role?: 'organizer' | 'member';

  /**
   * Whether to exclude current user from list
   *
   * @default false
   */
  excludeCurrentUser?: boolean;
}

const containerTokens: IStackTokens = {
  childrenGap: 16,
};

const personaStyles: Partial<IPersonaStyles> = {
  primaryText: {
    fontWeight: FontWeights.semibold,
  },
};

export const FamilyPersonaList: React.FC<IFamilyPersonaListProps> = (props: IFamilyPersonaListProps) => {
  const { role, excludeCurrentUser = false } = props;

  const { t } = useLocalizedStrings();
  const dispatch = useAppDispatch();
  const familyMembers = useAppSelector((state) => state.family.familyMembers);

  useEffect(() => {
    dispatch(getFamilyMembersAsync());
  }, [dispatch]);

  if (!familyMembers) {
    // TODO: render shimmers
    return null;
  }

  const filteredMembers = role
    ? familyMembers.filter((member) => {
        if (excludeCurrentUser && pageDataService.isCurrentUserByUserId(member.puid)) {
          return false;
        }
        if (role === 'organizer') {
          return member.role === 'Admin';
        }
        return member.role === 'User' || member.role === 'Unknown';
      })
    : familyMembers;

  return (
    <Stack tokens={containerTokens}>
      {filteredMembers.map((member) => {
        const secondaryText = {
          Admin: t('FamilyPersonaList.Role_Admin'),
          User: t('FamilyPersonaList.Role_User'),
          Unknown: t('FamilyPersonaList.Role_Unknown'),
        }[member.role];

        return (
          <Persona
            key={member.puid}
            text={member.displayName}
            styles={personaStyles}
            secondaryText={secondaryText}
            imageUrl={member.photoUrl}
            size={PersonaSize.size40}
          />
        );
      })}
    </Stack>
  );
};
